/**
 * Piezas presentacionales del wallet de la Home (la orquestación de estado vive
 * en `WalletButton`). Portado de `home-screen.jsx` del bundle de diseño v7
 * (BalanceSheet / WalletHistoryScreen / TxnDetailSheet), pero alimentado con
 * `WalletTxn` reales (`walletModel`).
 *
 * El desglose de costos de una sesión NO se dibuja acá: la clicar una fila de
 * consumo, `WalletButton` abre el `SessionCostSheet` existente (datos reales).
 * Este archivo sólo dibuja el detalle de recargas/bonos (método · fecha ·
 * estado), que no tienen desglose por componente.
 */
import { useEffect, type ReactElement } from 'react';
import { formatOrbs } from '@orborix/shared-types';
import { groupByBucket, txnDateLabel, type WalletTxn } from './walletModel.js';
import './wallet.css';
import { useSheetDrag } from '../hooks/useSheetDrag.js';

// ─── Icons ────────────────────────────────────────────────────────────────────

/**
 * Orb-coin: el glifo propio de la moneda "orbs" — disco con canto en negativo
 * (una "o" gorda, declinación del isologo). Master en
 * `claude-design/orborix-brand/orbs/v1/`; acá inlineado con `currentColor`
 * para heredar los estados del pill (lime / ámbar / rojo).
 */
export function IconOrb({ size = 15 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" fillRule="evenodd">
      <path d="M12 1 A11 11 0 1 1 12 23 A11 11 0 1 1 12 1 Z M12 3.4 A8.6 8.6 0 1 1 12 20.6 A8.6 8.6 0 1 1 12 3.4 Z M12 5.1 A6.9 6.9 0 1 1 12 18.9 A6.9 6.9 0 1 1 12 5.1 Z" />
    </svg>
  );
}
function IconClose({ size = 18 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6 L 18 18 M18 6 L 6 18" />
    </svg>
  );
}
function IconPlus({ size = 16, color = 'currentColor' }: { size?: number; color?: string }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5 V 19 M5 12 H 19" />
    </svg>
  );
}
function IconChevR({ size = 15 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6 L 15 12 L 9 18" />
    </svg>
  );
}
function IconBack({ size = 18 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6 L 9 12 L 15 18" />
    </svg>
  );
}
function IconChat({ size = 17 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
      <path d="M21 12 a 9 9 0 0 1 -13 8 L 3 21 L 4 17 A 9 9 0 1 1 21 12 Z" />
    </svg>
  );
}
function IconTarget({ size = 17 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconHeadphones({ size = 17 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
      <path d="M4 14 V12 a8 8 0 0 1 16 0 v2" />
      <rect x="3" y="14" width="4" height="6" rx="1.6" />
      <rect x="17" y="14" width="4" height="6" rx="1.6" />
    </svg>
  );
}
// Mismo glifo "camino" que usa Activity para el Programa guiado (identidad naranja).
function IconPath({ size = 17 }: { size?: number }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 20 C 6 15 18 15 18 11 C 18 7 6 7 6 4" />
      <circle cx="6" cy="4" r="2" fill="currentColor" stroke="none" />
      <circle cx="18" cy="11" r="2" fill="currentColor" stroke="none" />
      <circle cx="6" cy="20" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Dos flechas en ronda: el repaso. */
function IconReview(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 11a8 8 0 0 0-14.9-4" />
      <path d="M4 4v4h4" />
      <path d="M4 13a8 8 0 0 0 14.9 4" />
      <path d="M20 20v-4h-4" />
    </svg>
  );
}

function TxnIcon({ kind }: { kind: WalletTxn['kind'] }): ReactElement {
  if (kind === 'conv')
    return (
      <span className="bal-txn-ic" style={{ background: 'rgba(167,139,250,0.16)', color: 'var(--purple)' }}>
        <IconChat />
      </span>
    );
  if (kind === 'scenario')
    return (
      <span className="bal-txn-ic" style={{ background: 'rgba(212,242,62,0.14)', color: 'var(--lime)' }}>
        <IconTarget />
      </span>
    );
  // Programa guiado: naranja, misma identidad que en Activity.
  if (kind === 'program')
    return (
      <span className="bal-txn-ic" style={{ background: 'rgba(255,157,108,0.14)', color: '#ff9d6c' }}>
        <IconPath />
      </span>
    );
  // Unlock de capítulo de la Audioteca (Fase 22): azul "escuchar".
  if (kind === 'unlock')
    return (
      <span className="bal-txn-ic" style={{ background: 'rgba(124,196,255,0.14)', color: '#7cc4ff' }}>
        <IconHeadphones />
      </span>
    );
  // Repaso de correcciones (Emmanuel, 15/10): lima, como el repaso.
  if (kind === 'review')
    return (
      <span className="bal-txn-ic" style={{ background: 'rgba(212,242,62,0.14)', color: 'var(--lime)' }}>
        <IconReview />
      </span>
    );
  // grant / refund / adjust → recargas y movimientos de saldo.
  return (
    <span className="bal-txn-ic" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text)' }}>
      <IconPlus />
    </span>
  );
}

// ─── Filas ──────────────────────────────────────────────────────────────────

interface TxnRowProps {
  txn: WalletTxn;
  now: Date;
  onClick: (txn: WalletTxn) => void;
}

function TxnRow({ txn, now, onClick }: TxnRowProps): ReactElement {
  return (
    <button type="button" className="bal-txn clickable" onClick={() => onClick(txn)}>
      <TxnIcon kind={txn.kind} />
      <span className="bal-txn-body">
        <span className="bal-txn-label">{txn.label}</span>
        <span className="bal-txn-meta">
          {txn.sub} · {txnDateLabel(txn.createdAt, now)}
        </span>
      </span>
      <span className={`bal-txn-amt ${txn.dir}`}>
        {txn.dir === 'in' ? '+' : '−'}
        {formatOrbs(txn.amountNano)}
      </span>
      <span className="bal-txn-chev">
        <IconChevR />
      </span>
    </button>
  );
}

// ─── Lock de scroll del body mientras hay un overlay abierto ──────────────────

function useBodyScrollLock(): void {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
}

// ─── Hoja de saldo (últimos movimientos) ──────────────────────────────────────

interface BalanceSheetProps {
  /** Saldo en orbs ya formateado (ej. "4.990"). */
  balanceOrbs: string;
  txns: WalletTxn[];
  loading: boolean;
  now: Date;
  onClose: () => void;
  onSeeAll: () => void;
  onTxn: (txn: WalletTxn) => void;
  /**
   * Opens the recharge sheet; `null` hides "Agregar saldo" (the app does not
   * offer recharging here — `isRechargeOffered`).
   */
  onAddFunds: (() => void) | null;
}

const RECENT_COUNT = 4;

export function BalanceSheet({
  balanceOrbs,
  txns,
  loading,
  now,
  onClose,
  onSeeAll,
  onTxn,
  onAddFunds,
}: BalanceSheetProps): ReactElement {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  useBodyScrollLock();
  const recent = txns.slice(0, RECENT_COUNT);

  return (
    <div className="bal-overlay" role="dialog" aria-modal="true" aria-label="Tu saldo" onClick={onClose}>
      <div className="bal-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="bal-handle sheet-grab" {...grabProps} />
        <div className="bal-sheet-head">
          <span className="tag" style={{ color: 'var(--text-muted)' }}>
            TU SALDO
          </span>
          <button type="button" className="bal-close" onClick={onClose} aria-label="Cerrar">
            <IconClose />
          </button>
        </div>

        <div className="bal-amount-row">
          <div className="bal-amount-wrap">
            <span className="bal-orb" aria-hidden>
              <IconOrb size={22} />
            </span>
            <span className="bal-amount">{balanceOrbs}</span>
          </div>
          {onAddFunds && <AddFundsButton onClick={onAddFunds} />}
        </div>

        <div className="tag bal-section-label">ÚLTIMOS MOVIMIENTOS</div>
        {loading && recent.length === 0 ? (
          <div className="bal-empty">cargando movimientos…</div>
        ) : recent.length === 0 ? (
          <div className="bal-empty">Todavía no hay movimientos.</div>
        ) : (
          <div>
            {recent.map((t) => (
              <TxnRow key={t.id} txn={t} now={now} onClick={onTxn} />
            ))}
          </div>
        )}

        {txns.length > RECENT_COUNT && (
          <button type="button" className="bal-seeall" onClick={onSeeAll}>
            Ver todas las transacciones
            <IconChevR />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Botón "Agregar saldo": abre la hoja de packs (`AddCreditsSheet`). El flujo de
 * compra real (Google Play Billing vía RevenueCat) es nativo; en web la hoja
 * informa que la recarga vive en la app (Fase 23, monetización (b)).
 */
function AddFundsButton({ onClick }: { onClick: () => void }): ReactElement {
  return (
    <button
      type="button"
      className="bal-addbtn"
      title="Cargar orbs"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <IconPlus size={16} color="#0c0d15" /> Agregar saldo
    </button>
  );
}

// ─── Pantalla: historial completo ─────────────────────────────────────────────

interface WalletHistoryScreenProps {
  /** Saldo en orbs ya formateado (ej. "4.990"). */
  balanceOrbs: string;
  txns: WalletTxn[];
  loading: boolean;
  now: Date;
  onBack: () => void;
  onTxn: (txn: WalletTxn) => void;
  /**
   * Opens the recharge sheet; `null` hides "Agregar saldo" (the app does not
   * offer recharging here — `isRechargeOffered`).
   */
  onAddFunds: (() => void) | null;
}

export function WalletHistoryScreen({
  balanceOrbs,
  txns,
  loading,
  now,
  onBack,
  onTxn,
  onAddFunds,
}: WalletHistoryScreenProps): ReactElement {
  useBodyScrollLock();
  const groups = groupByBucket(txns, now);

  return (
    <div className="wallet-screen" role="dialog" aria-modal="true" aria-label="Transacciones">
      <div className="wallet-head">
        <button type="button" className="icon-btn" onClick={onBack} aria-label="Volver">
          <IconBack />
        </button>
        <span className="script wallet-head-title">Transacciones</span>
        <span style={{ width: 40 }} />
      </div>

      <div className="wallet-scroll hide-scroll">
        <div className="wallet-balance-strip">
          <div>
            <div className="tag" style={{ color: 'var(--text-faint)', marginBottom: 4 }}>
              SALDO ACTUAL
            </div>
            <div className="bal-amount-wrap">
              <span className="bal-orb" aria-hidden>
                <IconOrb size={15} />
              </span>
              <span className="wallet-strip-amount">{balanceOrbs}</span>
            </div>
          </div>
          {onAddFunds && <AddFundsButton onClick={onAddFunds} />}
        </div>

        {loading && groups.length === 0 ? (
          <div className="bal-empty">cargando movimientos…</div>
        ) : groups.length === 0 ? (
          <div className="bal-empty">Todavía no hay movimientos.</div>
        ) : (
          groups.map((g) => (
            <div key={g.bucket} style={{ marginBottom: 10 }}>
              <div className="wallet-group-label">{g.bucket}</div>
              <div>
                {g.items.map((t) => (
                  <TxnRow key={t.id} txn={t} now={now} onClick={onTxn} />
                ))}
              </div>
            </div>
          ))
        )}
        <div style={{ height: 16 }} />
      </div>
    </div>
  );
}

// ─── Hoja de detalle de una recarga / bono / ajuste ──────────────────────────

interface TxnDetailSheetProps {
  txn: WalletTxn;
  now: Date;
  onClose: () => void;
}

export function TxnDetailSheet({ txn, now, onClose }: TxnDetailSheetProps): ReactElement {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  useBodyScrollLock();
  const typeLabel =
    txn.kind === 'refund'
      ? 'Reembolso'
      : txn.kind === 'adjust'
        ? 'Ajuste'
        : txn.kind === 'unlock'
          ? 'Capítulo de la Audioteca'
          : txn.kind === 'review'
            ? 'Repaso de correcciones'
            : 'Recarga';

  return (
    <div
      className="bal-overlay txn-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={txn.label}
      onClick={onClose}
    >
      <div className="bal-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="bal-handle sheet-grab" {...grabProps} />
        <div className="bal-sheet-head">
          <div className="txn-detail-id">
            <TxnIcon kind={txn.kind} />
            <div className="bal-txn-body">
              <div className="bal-txn-label" style={{ fontSize: 15 }}>
                {txn.label}
              </div>
              <div className="bal-txn-meta">{txnDateLabel(txn.createdAt, now)}</div>
            </div>
          </div>
          <button type="button" className="bal-close" onClick={onClose} aria-label="Cerrar">
            <IconClose />
          </button>
        </div>

        <div className="txn-detail-amount-wrap">
          <span className={`bal-txn-amt ${txn.dir} txn-detail-amount`}>
            <span className="bal-orb bal-orb--inline" aria-hidden>
              <IconOrb size={16} />
            </span>
            {txn.dir === 'in' ? '+' : '−'}
            {formatOrbs(txn.amountNano)}
          </span>
        </div>

        <div className="txn-bk-meta-card">
          <div className="txn-bk-meta-row">
            <span>Tipo</span>
            <b>{typeLabel}</b>
          </div>
          {txn.meta?.method && (
            <div className="txn-bk-meta-row">
              <span>Detalle</span>
              <b>{txn.meta.method}</b>
            </div>
          )}
          <div className="txn-bk-meta-row">
            <span>Fecha</span>
            <b>{txnDateLabel(txn.createdAt, now)}</b>
          </div>
          <div className="txn-bk-meta-row">
            <span>Estado</span>
            <b style={{ color: 'var(--lime)' }}>{txn.meta?.status ?? 'Completado'}</b>
          </div>
        </div>
      </div>
    </div>
  );
}
